import {FocusEvent, KeyboardEvent, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ChevronLeftIcon} from 'src/components/icons/ChevronLeftIcon';
import {Spinner} from 'src/components/layout/loader/Spinner';
import {useMatchMedia} from 'src/hooks/useMatchMedia';
import {SearchIcon} from '../../icons/SearchIcon';
import './CategorizedDropdown.scss';

export interface CategorizedDropdownOption {
    id: string;
    label: string;
}

export interface CategorizedDropdownCategory {
    id: string;
    label: string;
    options: Array<CategorizedDropdownOption>;
}

interface CategorizedDropdownProps {
    id: string;
    categories: Array<CategorizedDropdownCategory>;
    placeholder?: string;
    minSearchLength?: number;
    disableSearch?: boolean;
    disableCategories?: boolean;
    searchOnly?: boolean;
    openPanelOnFocus?: boolean;
    isLoading?: boolean;
    noResultsText: string;
    onSelect(option: CategorizedDropdownOption, category: CategorizedDropdownCategory): void;
    onCategorySelect?(category: CategorizedDropdownCategory): void;
    selectableCategoryIds?: Array<string>;
    flatOptionCategoryIds?: Array<string>;
    onQueryChange?(query: string): void;
}

export function CategorizedDropdown({
    id,
    categories,
    placeholder,
    minSearchLength = 3,
    disableSearch = false,
    disableCategories = false,
    searchOnly = false,
    openPanelOnFocus = true,
    isLoading = false,
    noResultsText,
    onSelect,
    onCategorySelect,
    selectableCategoryIds = [],
    flatOptionCategoryIds = [],
    onQueryChange
}: CategorizedDropdownProps) {
    const {t} = useTranslation();
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [hasUserTyped, setHasUserTyped] = useState(false);
    const [selectedOptionLabel, setSelectedOptionLabel] = useState('');
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [pendingMobileSelectionKeys, setPendingMobileSelectionKeys] = useState<Array<string>>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const mobileInputRef = useRef<HTMLInputElement>(null);
    const isInteractingInsideRef = useRef(false);

    const normalizedQuery = query.trim().toLowerCase();
    const isSearchEnabled = !disableSearch;
    const isCategoryNavigationEnabled = !disableCategories;
    const isRegularSelectMode = !isSearchEnabled && !isCategoryNavigationEnabled;
    const isSearchMode = isSearchEnabled && hasUserTyped && normalizedQuery.length >= minSearchLength;
    const isCompactOrMediumViewport = useMatchMedia('(max-width: 37.5rem)');
    const useNativeRegularSelect = isRegularSelectMode && isCompactOrMediumViewport;
    const useMobileFullscreenPanel = isCompactOrMediumViewport && !isRegularSelectMode;
    const listId = `${id}-list`;

    const flattenedOptions = useMemo(() => {
        return categories.flatMap((category) => {
            return category.options.map((option) => {
                return {
                    option,
                    category
                };
            });
        });
    }, [categories]);

    const selectedCategory = useMemo(() => {
        if (!selectedCategoryId) {
            return null;
        }
        return categories.find(({id: categoryId}) => categoryId === selectedCategoryId) || null;
    }, [categories, selectedCategoryId]);

    const selectableCategoryIdSet = useMemo(() => {
        return new Set(selectableCategoryIds);
    }, [selectableCategoryIds]);

    const flatOptionCategoryIdSet = useMemo(() => {
        return new Set(flatOptionCategoryIds);
    }, [flatOptionCategoryIds]);

    const hasSelectableItemsWithoutSearching = useMemo(() => {
        if (searchOnly) {
            return false;
        }

        if (!isCategoryNavigationEnabled) {
            return flattenedOptions.length > 0;
        }

        return categories.some((category) => {
            return selectableCategoryIdSet.has(category.id) || category.options.length > 0;
        });
    }, [categories, flattenedOptions, isCategoryNavigationEnabled, searchOnly, selectableCategoryIdSet]);

    const shouldAutoFocusMobileSearch =
        useMobileFullscreenPanel && isSearchEnabled && !hasSelectableItemsWithoutSearching;

    const [selectedRegularOptionKey, setSelectedRegularOptionKey] = useState('');

    const optionKeyMap = useMemo(() => {
        return new Map(
            flattenedOptions.map(({option, category}) => [
                `${category.id}::${option.id}`,
                {
                    option,
                    category
                }
            ])
        );
    }, [flattenedOptions]);

    const pendingMobileSelectionKeySet = useMemo(() => {
        return new Set(pendingMobileSelectionKeys);
    }, [pendingMobileSelectionKeys]);

    const pendingSelectedOptionIdSet = useMemo(() => {
        return pendingMobileSelectionKeys.reduce((selectedOptionIds, optionKey) => {
            const [, optionId] = optionKey.split('::');

            if (optionId) {
                selectedOptionIds.add(optionId);
            }

            return selectedOptionIds;
        }, new Set<string>());
    }, [pendingMobileSelectionKeys]);

    const pendingFlatSelectedOptionIdSet = useMemo(() => {
        return pendingMobileSelectionKeys.reduce((selectedOptionIds, optionKey) => {
            const [categoryId, optionId] = optionKey.split('::');

            if (!categoryId || !optionId) {
                return selectedOptionIds;
            }

            if (flatOptionCategoryIdSet.has(categoryId)) {
                selectedOptionIds.add(optionId);
            }

            return selectedOptionIds;
        }, new Set<string>());
    }, [flatOptionCategoryIdSet, pendingMobileSelectionKeys]);

    const getOptionKey = (option: CategorizedDropdownOption, category: CategorizedDropdownCategory) => {
        return `${category.id}::${option.id}`;
    };

    const isOptionPending = (option: CategorizedDropdownOption, category: CategorizedDropdownCategory) => {
        if (!useMobileFullscreenPanel) {
            return false;
        }

        if (pendingMobileSelectionKeySet.has(getOptionKey(option, category))) {
            return true;
        }

        if (flatOptionCategoryIdSet.has(category.id)) {
            return pendingSelectedOptionIdSet.has(option.id);
        }

        return pendingFlatSelectedOptionIdSet.has(option.id);
    };

    const searchResults = useMemo(() => {
        if (!isSearchMode) {
            return [] as Array<CategorizedDropdownCategory>;
        }

        return categories
            .map((category) => {
                return {
                    ...category,
                    options: category.options.filter(({label: optionLabel}) =>
                        optionLabel.toLowerCase().includes(normalizedQuery)
                    )
                } satisfies CategorizedDropdownCategory;
            })
            .filter(({options}) => options.length > 0);
    }, [categories, isSearchMode, normalizedQuery]);

    useEffect(() => {
        onQueryChange?.(isSearchEnabled ? query : '');
    }, [isSearchEnabled, onQueryChange, query]);

    useEffect(() => {
        if (!useMobileFullscreenPanel || !isOpen) {
            return;
        }

        if (shouldAutoFocusMobileSearch) {
            requestAnimationFrame(() => {
                mobileInputRef.current?.focus();
            });
        } else {
            mobileInputRef.current?.blur();
        }
    }, [isOpen, shouldAutoFocusMobileSearch, useMobileFullscreenPanel]);

    useEffect(() => {
        const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
            if (!containerRef.current) {
                return;
            }

            if (useMobileFullscreenPanel && isOpen) {
                return;
            }

            if (!containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
                setSelectedCategoryId(null);
                setPendingMobileSelectionKeys([]);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('touchstart', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('touchstart', handleOutsideClick);
        };
    }, [isOpen, useMobileFullscreenPanel]);

    useEffect(() => {
        if (!isOpen || !useMobileFullscreenPanel) {
            return;
        }

        setPendingMobileSelectionKeys([]);
    }, [isOpen, useMobileFullscreenPanel]);

    const handleOptionSelect = (option: CategorizedDropdownOption, category: CategorizedDropdownCategory) => {
        setQuery('');
        setHasUserTyped(false);
        setIsOpen(false);
        setSelectedCategoryId(null);
        setPendingMobileSelectionKeys([]);

        if (isRegularSelectMode) {
            setSelectedOptionLabel(option.label);
            setSelectedRegularOptionKey(`${category.id}::${option.id}`);
        }

        onSelect(option, category);
    };

    const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
            setSelectedCategoryId(null);
            setPendingMobileSelectionKeys([]);
            if (useMobileFullscreenPanel) {
                setQuery('');
                setHasUserTyped(false);
            }
            return;
        }

        if (
            isRegularSelectMode &&
            (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp')
        ) {
            e.preventDefault();
            setSelectedCategoryId(null);
            setIsOpen(true);
        }
    };

    const handleControlClick = () => {
        if (isRegularSelectMode) {
            setSelectedCategoryId(null);
            setIsOpen((prevIsOpen) => !prevIsOpen);
            return;
        }

        if (useMobileFullscreenPanel) {
            setSelectedCategoryId(null);
            setPendingMobileSelectionKeys([]);
            setIsOpen(true);
            return;
        }

        inputRef.current?.focus();

        if (openPanelOnFocus || isSearchMode || !isSearchEnabled) {
            setIsOpen(true);
        }
    };

    const handleContainerBlur = (e: FocusEvent<HTMLDivElement>) => {
        if (useMobileFullscreenPanel) {
            return;
        }

        if (isInteractingInsideRef.current) {
            return;
        }

        const nextFocusedElement = e.relatedTarget as Node | null;

        if (nextFocusedElement && e.currentTarget.contains(nextFocusedElement)) {
            return;
        }

        setIsOpen(false);
        setSelectedCategoryId(null);
        setPendingMobileSelectionKeys([]);
    };

    const handleMobileCancel = () => {
        setQuery('');
        setHasUserTyped(false);
        setSelectedCategoryId(null);
        setPendingMobileSelectionKeys([]);
        setIsOpen(false);
    };

    const handleOptionPress = (option: CategorizedDropdownOption, category: CategorizedDropdownCategory) => {
        if (useMobileFullscreenPanel) {
            const optionKey = getOptionKey(option, category);
            const isFlatOptionCategory = flatOptionCategoryIdSet.has(category.id);

            setPendingMobileSelectionKeys((previousKeys) => {
                if (previousKeys.includes(optionKey)) {
                    return previousKeys.filter((key) => key !== optionKey);
                }

                if (isFlatOptionCategory) {
                    const matchingNonFlatKeys = previousKeys.filter((key) => {
                        const [selectedCategoryId, selectedOptionId] = key.split('::');

                        return (
                            selectedOptionId === option.id &&
                            Boolean(selectedCategoryId) &&
                            !flatOptionCategoryIdSet.has(selectedCategoryId)
                        );
                    });

                    if (matchingNonFlatKeys.length > 0) {
                        return previousKeys.filter((key) => !matchingNonFlatKeys.includes(key));
                    }
                }

                if (!isFlatOptionCategory) {
                    const matchingFlatKeys = previousKeys.filter((key) => {
                        const [selectedCategoryId, selectedOptionId] = key.split('::');

                        return (
                            selectedOptionId === option.id &&
                            Boolean(selectedCategoryId) &&
                            flatOptionCategoryIdSet.has(selectedCategoryId)
                        );
                    });

                    if (matchingFlatKeys.length > 0) {
                        return previousKeys.filter((key) => !matchingFlatKeys.includes(key));
                    }
                }

                return [...previousKeys, optionKey];
            });
            return;
        }

        handleOptionSelect(option, category);
    };

    const handleSearchInputChange = (nextQuery: string) => {
        if (!isSearchEnabled) {
            setIsOpen(true);
            return;
        }

        setQuery(nextQuery);
        setHasUserTyped(true);
        setSelectedCategoryId(null);

        if (searchOnly && !useMobileFullscreenPanel) {
            setIsOpen(nextQuery.trim().length >= minSearchLength);
            return;
        }

        setIsOpen(true);
    };

    const handleMobileConfirm = () => {
        if (pendingMobileSelectionKeys.length === 0) {
            return;
        }

        pendingMobileSelectionKeys.forEach((optionKey) => {
            const selected = optionKeyMap.get(optionKey);

            if (selected) {
                onSelect(selected.option, selected.category);
            }
        });

        setQuery('');
        setHasUserTyped(false);
        setSelectedCategoryId(null);
        setPendingMobileSelectionKeys([]);
        setIsOpen(false);
    };

    const mobileConfirmButtonLabel = `${t('button_select.text')}${
        pendingMobileSelectionKeys.length > 0 ? ` (${pendingMobileSelectionKeys.length})` : ''
    }`;

    const renderCategories = () => {
        return (
            <ul className='categorized-dropdown__list'>
                {categories.flatMap((category) => {
                    if (flatOptionCategoryIdSet.has(category.id)) {
                        return category.options.map((option) => {
                            const isSelected = isOptionPending(option, category);
                            return (
                                <li key={`${category.id}-${option.id}`}>
                                    <button
                                        type='button'
                                        className={`categorized-dropdown__item-button categorized-dropdown__item-button--flat${isSelected ? ' categorized-dropdown__item-button--selected' : ''}`}
                                        onClick={() => {
                                            handleOptionPress(option, category);
                                        }}
                                    >
                                        {option.label}
                                    </button>
                                </li>
                            );
                        });
                    }

                    const isCategorySelectable = selectableCategoryIdSet.has(category.id);

                    return [
                        <li key={category.id}>
                            <button
                                type='button'
                                className='categorized-dropdown__item-button categorized-dropdown__item-button--category'
                                aria-haspopup={isCategorySelectable ? undefined : true}
                                aria-expanded={isCategorySelectable ? undefined : selectedCategoryId === category.id}
                                aria-controls={isCategorySelectable ? undefined : `${id}-${category.id}-options`}
                                onClick={() => {
                                    if (isCategorySelectable) {
                                        setQuery('');
                                        setHasUserTyped(false);
                                        setSelectedCategoryId(null);
                                        setPendingMobileSelectionKeys([]);
                                        setIsOpen(false);
                                        onCategorySelect?.(category);
                                        return;
                                    }

                                    setSelectedCategoryId(category.id);
                                }}
                            >
                                <span>{category.label}</span>
                                <span className='categorized-dropdown__category-chevron' aria-hidden='true'>
                                    <ChevronLeftIcon />
                                </span>
                            </button>
                        </li>
                    ];
                })}
            </ul>
        );
    };

    const renderFlatOptions = () => {
        if (flattenedOptions.length === 0) {
            return <p className='categorized-dropdown__empty'>{noResultsText}</p>;
        }

        return (
            <ul className='categorized-dropdown__list'>
                {flattenedOptions.map(({option, category}) => {
                    const isSelected = isOptionPending(option, category);
                    return (
                        <li key={`${category.id}-${option.id}`}>
                            <button
                                type='button'
                                className={`categorized-dropdown__item-button categorized-dropdown__item-button--flat${isSelected ? ' categorized-dropdown__item-button--selected' : ''}`}
                                onClick={() => {
                                    handleOptionPress(option, category);
                                }}
                            >
                                {option.label}
                            </button>
                        </li>
                    );
                })}
            </ul>
        );
    };

    const renderCategoryOptions = () => {
        if (!selectedCategory) {
            return null;
        }

        return (
            <>
                <button
                    type='button'
                    className='categorized-dropdown__back-button'
                    onClick={() => {
                        setSelectedCategoryId(null);
                    }}
                >
                    <span className='categorized-dropdown__back-icon'>
                        <ChevronLeftIcon />
                    </span>
                    <span className='categorized-dropdown__back-label'>{selectedCategory.label}</span>
                </button>
                <ul className='categorized-dropdown__list' id={`${id}-${selectedCategory.id}-options`}>
                    {selectedCategory.options.map((option) => {
                        const isSelected = isOptionPending(option, selectedCategory);
                        return (
                            <li key={option.id}>
                                <button
                                    type='button'
                                    className={`categorized-dropdown__item-button${isSelected ? ' categorized-dropdown__item-button--selected' : ''}`}
                                    onClick={() => {
                                        handleOptionPress(option, selectedCategory);
                                    }}
                                >
                                    {option.label}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </>
        );
    };

    const renderSearchResults = () => {
        if (isLoading && searchResults.length === 0) {
            return (
                <div className='categorized-dropdown__loader' role='status' aria-live='polite' aria-busy='true'>
                    <Spinner />
                </div>
            );
        }

        if (searchResults.length === 0) {
            return (
                <p className='categorized-dropdown__empty' role='status' aria-live='polite'>
                    {noResultsText}
                </p>
            );
        }

        return (
            <div className='categorized-dropdown__search-results'>
                {searchResults.map((category) => {
                    return (
                        <section className='categorized-dropdown__category-group' key={category.id}>
                            <h3>{category.label}</h3>
                            <ul className='categorized-dropdown__list'>
                                {category.options.map((option) => {
                                    const isSelected = isOptionPending(option, category);
                                    return (
                                        <li key={option.id}>
                                            <button
                                                type='button'
                                                className={`categorized-dropdown__item-button${isSelected ? ' categorized-dropdown__item-button--selected' : ''}`}
                                                onClick={() => {
                                                    handleOptionPress(option, category);
                                                }}
                                            >
                                                {option.label}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </section>
                    );
                })}
            </div>
        );
    };

    const renderFlatSearchResults = () => {
        if (isLoading && searchResults.length === 0) {
            return (
                <div className='categorized-dropdown__loader' role='status' aria-live='polite' aria-busy='true'>
                    <Spinner />
                </div>
            );
        }

        const flattenedSearchResults = searchResults.flatMap((category) => {
            return category.options.map((option) => {
                return {
                    option,
                    category
                };
            });
        });

        if (flattenedSearchResults.length === 0) {
            return (
                <p className='categorized-dropdown__empty' role='status' aria-live='polite'>
                    {noResultsText}
                </p>
            );
        }

        return (
            <ul className='categorized-dropdown__list'>
                {flattenedSearchResults.map(({option, category}) => {
                    const isSelected = isOptionPending(option, category);
                    return (
                        <li key={`${category.id}-${option.id}`}>
                            <button
                                type='button'
                                className={`categorized-dropdown__item-button categorized-dropdown__item-button--flat${isSelected ? ' categorized-dropdown__item-button--selected' : ''}`}
                                onClick={() => {
                                    handleOptionPress(option, category);
                                }}
                            >
                                {option.label}
                            </button>
                        </li>
                    );
                })}
            </ul>
        );
    };

    const renderDropdownBody = () => {
        if (isSearchMode) {
            return isCategoryNavigationEnabled ? renderSearchResults() : renderFlatSearchResults();
        }

        if (searchOnly) {
            return null;
        }

        if (!isCategoryNavigationEnabled) {
            return renderFlatOptions();
        }

        if (selectedCategoryId) {
            return renderCategoryOptions();
        }

        return renderCategories();
    };

    if (useNativeRegularSelect) {
        return (
            <div className='categorized-dropdown'>
                <select
                    id={id}
                    className='categorized-dropdown__native-select'
                    value={selectedRegularOptionKey}
                    onChange={(e) => {
                        const selected = optionKeyMap.get(e.target.value);

                        if (!selected) {
                            return;
                        }

                        handleOptionSelect(selected.option, selected.category);
                    }}
                >
                    <option value='' disabled>
                        {placeholder || ''}
                    </option>
                    {flattenedOptions.map(({option, category}) => {
                        return (
                            <option key={`${category.id}-${option.id}`} value={`${category.id}::${option.id}`}>
                                {option.label}
                            </option>
                        );
                    })}
                </select>
            </div>
        );
    }

    return (
        <div
            className='categorized-dropdown'
            ref={containerRef}
            onBlur={handleContainerBlur}
            onMouseDownCapture={() => {
                isInteractingInsideRef.current = true;
                setTimeout(() => {
                    isInteractingInsideRef.current = false;
                }, 0);
            }}
            onTouchStartCapture={() => {
                isInteractingInsideRef.current = true;
                setTimeout(() => {
                    isInteractingInsideRef.current = false;
                }, 0);
            }}
        >
            <div
                className={`categorized-dropdown__input-wrap${!isSearchEnabled ? ' categorized-dropdown__input-wrap--no-search' : ''}${isRegularSelectMode ? ' categorized-dropdown__input-wrap--regular-select' : ''}${isOpen ? ' categorized-dropdown__input-wrap--open' : ''}`}
                onClick={handleControlClick}
                role='button'
                tabIndex={0}
                aria-expanded={isOpen}
                onKeyDown={(event) => {
                    if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                        event.preventDefault();
                        handleControlClick();
                    }
                }}
            >
                {isSearchEnabled && <SearchIcon />}
                <input
                    ref={inputRef}
                    id={id}
                    type='text'
                    className={`categorized-dropdown__input${isRegularSelectMode ? ' categorized-dropdown__input--regular-select' : ''}`}
                    value={isRegularSelectMode ? selectedOptionLabel : query}
                    placeholder={isRegularSelectMode && selectedOptionLabel ? undefined : placeholder}
                    readOnly={!isSearchEnabled || (useMobileFullscreenPanel && !isOpen)}
                    autoComplete='off'
                    aria-controls={listId}
                    aria-haspopup='true'
                    aria-autocomplete={isSearchEnabled ? 'list' : undefined}
                    onFocus={() => {
                        if (isRegularSelectMode) {
                            return;
                        }

                        if (useMobileFullscreenPanel) {
                            setIsOpen(true);
                            return;
                        }

                        if (openPanelOnFocus || isSearchMode || !isSearchEnabled) {
                            setIsOpen(true);
                        }
                    }}
                    onChange={(e) => {
                        handleSearchInputChange(e.target.value);
                    }}
                    onKeyDown={handleInputKeyDown}
                />
                {isRegularSelectMode && (
                    <span className='categorized-dropdown__select-chevron' aria-hidden='true'>
                        <ChevronLeftIcon />
                    </span>
                )}
            </div>

            {isOpen && useMobileFullscreenPanel && (
                <div className='categorized-dropdown__mobile-panel-wrapper' role='dialog' aria-modal='true'>
                    <div className='categorized-dropdown__mobile-header'>
                        <div
                            className={`categorized-dropdown__input-wrap categorized-dropdown__input-wrap--mobile${!isSearchEnabled ? ' categorized-dropdown__input-wrap--no-search' : ''}`}
                        >
                            {isSearchEnabled && <SearchIcon />}
                            <input
                                ref={mobileInputRef}
                                id={`${id}-mobile-input`}
                                type='text'
                                className='categorized-dropdown__input'
                                value={query}
                                placeholder={placeholder}
                                readOnly={!isSearchEnabled}
                                autoComplete='off'
                                aria-controls={listId}
                                aria-haspopup='true'
                                aria-autocomplete={isSearchEnabled ? 'list' : undefined}
                                onChange={(e) => {
                                    handleSearchInputChange(e.target.value);
                                }}
                                onKeyDown={handleInputKeyDown}
                            />
                        </div>
                        <button
                            type='button'
                            className='categorized-dropdown__mobile-cancel'
                            onClick={handleMobileCancel}
                        >
                            {t('button_cancel.text')}
                        </button>
                    </div>
                    <div id={listId} className='categorized-dropdown__mobile-panel' aria-busy={isLoading}>
                        {renderDropdownBody()}
                    </div>
                    <div className='categorized-dropdown__mobile-actions'>
                        <button
                            type='button'
                            className='categorized-dropdown__mobile-confirm'
                            disabled={pendingMobileSelectionKeys.length === 0}
                            onClick={handleMobileConfirm}
                        >
                            {mobileConfirmButtonLabel}
                        </button>
                    </div>
                </div>
            )}

            {isOpen && !useMobileFullscreenPanel && (
                <div id={listId} className='categorized-dropdown__panel' aria-busy={isLoading}>
                    {renderDropdownBody()}
                </div>
            )}
        </div>
    );
}

// to be sure no more time is wasted looking for this...
export const CategorizedDropDown = CategorizedDropdown;
